import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Message } from 'semantic-ui-react';

export type ContractRejectionModalKind = 'cqv' | 'qualidade' | 'ccv' | 'cvp' | 'trocaCarteira';

interface RejectContractModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (rejectionReason: string) => Promise<void>;
  /** Qual tipo de recusa (afeta apenas título e placeholder da modal). Default: CQV */
  rejectionKind?: ContractRejectionModalKind;
}

export const RejectContractModal: React.FC<RejectContractModalProps> = ({
  open,
  onClose,
  onConfirm,
  rejectionKind = 'cqv',
}) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setRejectionReason('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    setError(null);

    const trimmedReason = rejectionReason.trim();
    
    if (!trimmedReason) {
      setError('O motivo da recusa é obrigatório.');
      return;
    }

    if (trimmedReason.length < 10) {
      setError('O motivo da recusa deve ter no mínimo 10 caracteres.');
      return;
    }

    if (trimmedReason.length > 500) {
      setError('O motivo da recusa deve ter no máximo 500 caracteres.');
      return;
    }

    setLoading(true);
    try {
      await onConfirm(trimmedReason);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao recusar contrato. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRejectionReason('');
    setError(null);
    onClose();
  };

  const trimmedLength = rejectionReason.trim().length;

  const headerTitle =
    rejectionKind === 'qualidade'
      ? 'Recusar Contrato — Qualidade'
      : rejectionKind === 'ccv'
        ? 'Recusar Contrato — CCV'
        : rejectionKind === 'cvp'
          ? 'Recusar Contrato — CVP'
          : rejectionKind === 'trocaCarteira'
            ? 'Recusado Troca de Carteira'
            : 'Recusar Contrato CQV';

  const placeholderText =
    rejectionKind === 'qualidade'
      ? 'Descreva o motivo da recusa na Qualidade (mínimo 10 caracteres, máximo 500 caracteres)'
      : rejectionKind === 'ccv'
        ? 'Descreva o motivo da recusa no CCV (mínimo 10 caracteres, máximo 500 caracteres)'
        : rejectionKind === 'cvp'
          ? 'Descreva o motivo da recusa no CVP (mínimo 10 caracteres, máximo 500 caracteres)'
          : rejectionKind === 'trocaCarteira'
            ? 'Descreva o motivo da não autorização da troca de carteira (mínimo 10 caracteres, máximo 500 caracteres). O supervisor será notificado por e-mail.'
            : 'Descreva o motivo da recusa do contrato (mínimo 10 caracteres, máximo 500 caracteres)';

  return (
    <Modal open={open} onClose={handleClose} size="small">
      <Modal.Header>{headerTitle}</Modal.Header>
      <Modal.Content>
        {error && (
          <Message negative>
            <Message.Header>Erro</Message.Header>
            <p>{error}</p>
          </Message>
        )}
        
        <Form>
          <Form.Field>
            <label>Motivo da Recusa <span style={{ color: 'red' }}>*</span></label>
            <textarea
              placeholder={placeholderText}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              disabled={loading}
              required
              rows={5}
              style={{ 
                resize: 'vertical',
                minHeight: '100px',
                fontFamily: 'inherit'
              }}
            />
            <div style={{ 
              marginTop: '0.5rem', 
              fontSize: '0.9em',
              color: trimmedLength < 10 || trimmedLength > 500 ? '#db2828' : '#666'
            }}>
              {trimmedLength}/500 caracteres
              {trimmedLength < 10 && trimmedLength > 0 && (
                <span style={{ marginLeft: '0.5rem', color: '#db2828' }}>
                  (mínimo 10 caracteres)
                </span>
              )}
            </div>
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button 
          negative 
          onClick={handleSubmit} 
          loading={loading}
          disabled={loading || trimmedLength < 10 || trimmedLength > 500}
        >
          Confirmar Recusa
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

